export const queryKeys = {
  appointments: {
    all: ['appointments'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.appointments.all, 'list', filters] as const,
    detail: (id: number) => [...queryKeys.appointments.all, 'detail', id] as const,
    /** AB#1797 — Appointments List V2 */
    listV2: (params?: Record<string, unknown>) =>
      [...queryKeys.appointments.all, 'listV2', params] as const,
    /** S1 (AB#2204) — a staff member's own booking (GET /salon-staff/appointments/{id}). */
    staffDetail: (id: number) => [...queryKeys.appointments.all, 'staffDetail', id] as const,
    /**
     * S2 (AB#2205) — the staff member's My Day (GET /salon-staff/my-day). Under
     * `appointments.all` on purpose: every booking action and mark-paid already
     * invalidates that prefix, so My Day refreshes after an action in the detail.
     */
    staffMyDay: (date?: string) => [...queryKeys.appointments.all, 'staffMyDay', date ?? 'today'] as const,
  },
  services: {
    all: ['services'] as const,
    list: (branchId?: number) => [...queryKeys.services.all, 'list', branchId] as const,
  },
  staff: {
    all: ['staff'] as const,
    list: () => [...queryKeys.staff.all, 'list'] as const,
  },
  /** S2 (AB#2205) — the signed-in staff member's own identity + schedule. */
  staffSelf: {
    all: ['staffSelf'] as const,
    me: () => ['staffSelf', 'me'] as const,
    schedule: (range?: { from?: string; to?: string }) =>
      ['staffSelf', 'schedule', range?.from ?? null, range?.to ?? null] as const,
  },
  business: {
    all: ['business'] as const,
    /** GET /business/my-business — current owner's business profile. */
    me: () => [...queryKeys.business.all, 'me'] as const,
    /** GET /business/{id} — explicit fetch by id (Super Admin etc.). */
    detail: (id?: number) => [...queryKeys.business.all, 'detail', id] as const,
    /** AB#1682 — GET /api/v2/business/holidays — caller's closures. */
    holidays: () => [...queryKeys.business.all, 'holidays'] as const,
    /**
     * Epic AB#1824 — GET /api/business/hours/effective — matrix-shaped
     * per-branch + default-row hours read. Single key (no params) because
     * the endpoint is tenant-scoped via JWT and always returns every
     * branch the caller owns.
     */
    hoursEffective: () =>
      [...queryKeys.business.all, 'hours', 'effective'] as const,
  },
  /** AB#1682 — GET /api/v2/business-types — master picker (cached 15 min on BE). */
  businessTypes: {
    all: ['businessTypes'] as const,
    list: () => ['businessTypes', 'list'] as const,
  },
  /** AB#1675 — GET /salon-owner/branches — caller's branch list. */
  // AB#893 — prepaid packages
  packages: {
    all: ['packages'] as const,
    list: () => ['packages', 'list'] as const,
  },
  branches: {
    all: ['branches'] as const,
    list: () => ['branches', 'list'] as const,
    /**
     * AB#1747 — GET /salon-owner/branches/{branchId}/hours — per-branch
     * working hours (7 rows per branch). Per-branch key so editing one
     * branch's hours doesn't invalidate the others' caches.
     */
    hours: (branchId: number) => ['branches', 'hours', branchId] as const,
  },
  /** CR-2026-05-15 — GET /category/business — caller's subscribed categories. */
  categories: {
    all: ['categories'] as const,
    business: () => ['categories', 'business'] as const,
    master: () => ['categories', 'master'] as const,
    requests: () => ['categories', 'requests'] as const,
  },
  /**
   * AB#1818 — Super Admin category management. Separate key from owner-side
   * `categories.*` because `/category/admin/all` returns pending + rejected
   * categories the owner endpoint hides; cache contents are not interchangeable.
   */
  adminCategories: {
    all: ['admin', 'categories'] as const,
    list: () => ['admin', 'categories', 'list'] as const,
    // AB#1871 — pre-deactivate impact counts, keyed per category id.
    impact: (categoryId: number) =>
      ['admin', 'categories', 'impact', categoryId] as const,
    // AB#1871 — lazy drill-down pages, keyed per category id + axis.
    impactSalons: (categoryId: number) =>
      ['admin', 'categories', 'impact', categoryId, 'salons'] as const,
    impactServices: (categoryId: number) =>
      ['admin', 'categories', 'impact', categoryId, 'services'] as const,
    impactBookings: (categoryId: number) =>
      ['admin', 'categories', 'impact', categoryId, 'bookings'] as const,
  },
  /**
   * P0-7 (AB#2047) — SuperAdmin read-only recent-bookings lookup, keyed per
   * business + filters so switching business or filters refetches in isolation.
   */
  adminBookings: {
    all: ['admin', 'bookings'] as const,
    list: (businessId: number, filters?: Record<string, unknown>) =>
      ['admin', 'bookings', businessId, filters] as const,
    // Batch C (AB#2195) — cross-salon search and a salon's open bookings; under
    // `admin.bookings` so a cancel invalidates every admin booking view at once.
    search: (params: Record<string, unknown>) => ['admin', 'bookings', 'search', params] as const,
    open: (businessId: number) => ['admin', 'bookings', 'open', businessId] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: () => [...queryKeys.notifications.all, 'list'] as const,
  },
  dashboard: {
    summary: (filters?: Record<string, unknown>) =>
      ['dashboard', 'summary', filters] as const,
  },
  subscription: {
    all: ['subscription'] as const,
    current: () => [...queryKeys.subscription.all, 'current'] as const,
    features: () => [...queryKeys.subscription.all, 'features'] as const,
  },
  /** AB#1732 — Super Admin Sponsored Placement CMS (FU-5). */
  sponsoredPlacements: {
    all: ['sponsoredPlacements'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.sponsoredPlacements.all, 'list', filters] as const,
  },
  /**
   * Calendar v1 Epic AB#1746 — C3 grid + C5 availability + C7 realtime root.
   * Invalidating `calendar.all` matches every descendant in one shot,
   * which the C7 SignalR subscriber uses on every `appointment.changed` event.
   */
  calendar: {
    all: ['calendar'] as const,
    grid: (params: { branchId: number; date: string }) =>
      [...queryKeys.calendar.all, 'grid', params] as const,
    availability: (params: Record<string, unknown>) =>
      [...queryKeys.calendar.all, 'availability', params] as const,
    /** AB#1754 — single-tenant per-business settings (no params). */
    settings: () => [...queryKeys.calendar.all, 'settings'] as const,
  },
  /** AB#1006 — Super Admin Platform Promotions (Devanza-funded, platform-scoped). */
  platformPromotions: {
    all: ['platformPromotions'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.platformPromotions.all, 'list', filters] as const,
    detail: (id: number) =>
      [...queryKeys.platformPromotions.all, 'detail', id] as const,
    analytics: (id: number) =>
      [...queryKeys.platformPromotions.all, 'analytics', id] as const,
    audit: (id: number) =>
      [...queryKeys.platformPromotions.all, 'audit', id] as const,
    config: () => [...queryKeys.platformPromotions.all, 'config'] as const,
  },
} as const;
